import { ChevronDown, Eye, Loader2, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../hooks/useAuth.js";
import { type ViewAsCandidate, authApi, clearViewAs, getViewAs, setViewAs } from "../../lib/api.js";

/**
 * "Ansicht als" — Admin-Schalter, um die Plattform aus Sicht eines Managers
 * (nur dessen Berichtslinie) anzusehen und zurückzuwechseln. Banner ist aktiv,
 * sobald ein Ziel gesetzt ist (unabhängig von /me, das dann "manager" liefert).
 */
export function ViewAsBar() {
  const { user } = useAuth();
  const active = getViewAs();
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState<ViewAsCandidate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  // WICHTIG: Der Banner-Fall MUSS vor dem Admin-Check stehen. Während der
  // Vorschau meldet /me nämlich (gewollt) role="manager" — stünde der
  // Admin-Check zuerst, verschwände die Leiste samt "Zurück zu Admin" und man
  // wäre in der Vorschau gefangen (Bug 2026-07-08). Missbrauch ist ausge-
  // schlossen: Der X-View-As-Header wirkt serverseitig nur für echte Admins,
  // und view_as liegt in sessionStorage (endet mit dem Browser).
  if (active) {
    return (
      <div
        className="flex items-center justify-between px-6 py-2 text-sm"
        style={{ background: "var(--accent)", color: "#fff" }}
      >
        <span className="flex items-center gap-2">
          <Eye size={15} /> Ansicht als <b>{active.name}</b> (Manager) — du siehst nur dessen
          Berichtslinie
        </span>
        <button
          type="button"
          onClick={() => {
            clearViewAs();
            window.location.reload();
          }}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
          style={{ background: "rgba(255,255,255,0.2)" }}
        >
          <X size={13} /> Zurück zu Admin
        </button>
      </div>
    );
  }

  // Das Auswahlmenü (unten) bleibt Admins vorbehalten.
  if (user?.role !== "admin") return null;

  async function openPicker() {
    setOpen((o) => !o);
    if (candidates === null) {
      setLoading(true);
      try {
        setCandidates((await authApi.viewAsCandidates()).candidates);
      } catch {
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    }
  }

  const filtered = (candidates ?? []).filter((c) =>
    c.name.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="relative px-6 py-1.5" style={{ borderBottom: "1px solid var(--border)" }}>
      <button
        type="button"
        onClick={openPicker}
        className="flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded"
        style={{ color: "var(--text-muted)", border: "1px solid var(--border)" }}
      >
        <Eye size={13} /> Ansicht als … <ChevronDown size={13} />
      </button>
      {open && (
        <div
          className="absolute left-6 mt-1 w-72 rounded-lg shadow-lg z-50 p-2"
          style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Manager suchen …"
            className="w-full px-2 py-1.5 rounded text-sm mb-2"
            style={{
              background: "var(--bg-hover)",
              color: "var(--text-primary)",
              border: "1px solid var(--border)",
            }}
          />
          <div className="max-h-72 overflow-y-auto">
            {loading && (
              <div
                className="flex items-center gap-2 text-sm px-2 py-2"
                style={{ color: "var(--text-muted)" }}
              >
                <Loader2 size={14} className="animate-spin" /> Lädt …
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-sm px-2 py-2" style={{ color: "var(--text-muted)" }}>
                Keine Manager gefunden.
              </div>
            )}
            {!loading &&
              filtered.map((c) => (
                <button
                  type="button"
                  key={c.personioEmployeeId}
                  onClick={() => {
                    setViewAs(c.personioEmployeeId, c.name);
                    window.location.reload();
                  }}
                  className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center justify-between"
                  style={{ color: "var(--text-primary)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--bg-hover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span>{c.name}</span>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                    {c.reportCount}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
